"use client";

import { TranslationKey, Translations } from "@/locales/types";
import { createContext, useContext, useEffect, useState } from "react";
import { useUser } from "./UserContext";
import { Language, LanguageContextType } from "./types";
import { logger } from "@/lib/logger";

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const LanguageProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [language, setLanguageState] = useState<Language>("es");
  const [translations, setTranslations] = useState<Translations | null>(null);
  const [isLoadingLanguage, setIsLoadingLanguage] = useState(true);

  // Obtener configuración del UserContext
  const { configuracion, isLoadingUser } = useUser();

  // Cargar traducciones dinámicamente cuando cambia el idioma
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        let langModule: { default: Translations };

        if (language === "es") {
          langModule = await import("@/locales/es.json");
        } else {
          langModule = await import("@/locales/en.json");
        }

        setTranslations(langModule.default as Translations);
      } catch (error) {
        console.error(
          `❌ Error cargando traducciones para ${language}:`,
          error
        );
        // Cargar español por defecto en caso de error
        if (language !== "es") {
          try {
            const esModule = await import("@/locales/es.json");
            setTranslations(esModule.default as Translations);
          } catch (fallbackError) {
            console.error(
              "❌ Error crítico cargando traducciones de respaldo:",
              fallbackError
            );
          }
        }
      }
    };

    loadTranslations();
  }, [language]);

  // Sincronizar idioma con la configuración del usuario
  useEffect(() => {
    if (!isLoadingUser && configuracion) {
      const userLanguage = configuracion.idioma;

      if (userLanguage === "es" || userLanguage === "en") {
        setLanguageState(userLanguage);
      } else {
        logger.warn(`⚠️ Idioma no soportado: ${userLanguage}, usando español`);
        setLanguageState("es");
      }
      setIsLoadingLanguage(false);
    } else if (!isLoadingUser && !configuracion) {
      // Si no hay configuración, usar español por defecto
      setLanguageState("es");
      setIsLoadingLanguage(false);
    }
  }, [configuracion, isLoadingUser]);

  // Función para cambiar el idioma manualmente
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  // Función de traducción con type safety completo que maneja múltiples niveles de anidación
  const t = (key: TranslationKey): string => {
    if (!translations) {
      return key;
    }

    const keys = key.split(".");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let result: any = translations;

    // Navegar por los niveles de anidación
    for (const k of keys) {
      if (result && typeof result === "object" && k in result) {
        result = result[k];
      } else {
        logger.warn(`⚠️ Traducción no encontrada para: ${key}`);
        return key;
      }
    }

    if (typeof result === "string") {
      return result;
    }

    logger.warn(`⚠️ Traducción no encontrada para: ${key} (no es string)`);
    return key;
  };

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, t, isLoadingLanguage }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);

  if (!context) {
    if (
      typeof window !== "undefined" &&
      process.env.NODE_ENV === "production"
    ) {
      logger.warn(
        "useLanguage: Contexto no encontrado, retornando valores por defecto"
      );
      return {
        language: "es",
        setLanguage: () => {},
        t: (key: TranslationKey) => key,
        isLoadingLanguage: false,
      };
    }
    throw new Error("useLanguage debe estar dentro del LanguageProvider");
  }

  return context;
};

// Hook simplificado para solo usar traducciones
export const useTranslation = () => {
  const { t } = useLanguage();
  return { t };
};
